// Copyright (c) Ping. All rights reserved.

import React, { useCallback } from 'react';
import { Pressable, Text, View, type TextStyle, type ViewStyle } from 'react-native';
import { tokens } from '../design-system/tokens';
import { useColorScheme } from '../foundations/colorScheme';
import { SettingsToggle } from '../settings-modal/components/SettingsToggle';
import { shareLocationStrings } from './strings';

export interface LiveLocationToggleProps {
  readonly enabled: boolean;
  readonly onChange: (next: boolean) => void;
  readonly durationSeconds: number;
  readonly onChangeDuration: (next: number) => void;
}

const OPTIONS: readonly { readonly value: number; readonly label: string }[] = [
  { value: 15 * 60, label: shareLocationStrings.live.duration15m },
  { value: 60 * 60, label: shareLocationStrings.live.duration1h },
  { value: 8 * 60 * 60, label: shareLocationStrings.live.duration8h },
] as const;

export const LiveLocationToggle: React.FC<LiveLocationToggleProps> = ({
  enabled,
  onChange,
  durationSeconds,
  onChangeDuration,
}) => {
  const scheme = useColorScheme();
  const colors = tokens.color.semantic[scheme];

  const handleSelect = useCallback(
    (next: number) => {
      onChangeDuration(next);
    },
    [onChangeDuration],
  );

  return (
    <View style={styles.wrap}>
      <SettingsToggle
        label={shareLocationStrings.live.label}
        description={shareLocationStrings.live.description}
        value={enabled}
        onChange={onChange}
      />
      {enabled ? (
        <View style={styles.options}>
          {OPTIONS.map((option) => {
            const selected = option.value === durationSeconds;
            return (
              <Pressable
                key={option.value}
                onPress={() => handleSelect(option.value)}
                accessibilityRole="radio"
                accessibilityLabel={option.label}
                accessibilityState={{ checked: selected }}
                style={[
                  styles.option,
                  {
                    borderColor: selected ? colors.accent.indigo : colors.border.primary,
                    backgroundColor: selected
                      ? colors.surface.secondary
                      : colors.surface.primary,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.optionText,
                    {
                      color: selected ? colors.accent.indigo : colors.text.primary,
                      fontWeight: selected ? '600' : '500',
                    },
                  ]}
                >
                  {option.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      ) : null}
    </View>
  );
};

const styles = {
  wrap: {
    gap: tokens.spacing.xs,
  } satisfies ViewStyle,
  options: {
    flexDirection: 'row',
    gap: tokens.spacing.xs,
    flexWrap: 'wrap',
  } satisfies ViewStyle,
  option: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    borderWidth: 1,
    borderRadius: tokens.radius.pill,
  } satisfies ViewStyle,
  optionText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.footnote,
  } satisfies TextStyle,
};
